import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal, MaskedLine } from "@/components/Reveal";

const sections = [
  {
    id: "no-collection",
    title: "1. Information Moove Does Not Collect",
    body: [
      "Moove does not require you to create an account or provide personal details.",
      "We do not collect:",
    ],
    bullets: [
      "Your name, email address, or phone number through the App",
      "Account credentials",
      "Precise or approximate location",
      "Contacts, photographs, camera, microphone, or calendar information",
      "Advertising identifiers",
      "Information for targeted advertising",
      "Your payment-card or banking details",
      "Your alarm settings or step counts on OneDayBuild servers",
    ],
    tail: "Moove does not sell personal information or use your information to track you across apps or websites.",
  },
  {
    id: "motion",
    title: "2. Motion and Fitness Information",
    body: [
      "Moove uses your device's pedometer and accelerometer capabilities to determine whether you have completed the number of steps required to dismiss an alarm.",
      "This information is used only to provide the App's core alarm functionality.",
      "Your movement information, step counts, alarm times, repeat settings, and step targets are processed and stored locally on your device. They are not uploaded to OneDayBuild's servers.",
      "Moove does not access your location to calculate your steps.",
      "You can control the App's access to Motion & Fitness data through your device settings. If you disable this permission, features that require step detection may not function correctly.",
    ],
  },
  {
    id: "notifications",
    title: "3. Alarm Notifications",
    body: [
      "Moove uses local notifications to alert you when an alarm is scheduled to ring.",
      "These notifications are generated and managed on your device. OneDayBuild does not operate a push-notification server for Moove.",
      "You can manage notification permissions through your device settings. Disabling notifications may prevent alarms or related alerts from operating as intended.",
    ],
  },
  {
    id: "diagnostics",
    title: "4. Diagnostic and Crash Information",
    body: [
      "Moove uses Sentry to identify crashes, errors, and technical problems. Depending on the nature of an error and your device settings, Sentry may process limited technical information such as:",
    ],
    bullets: [
      "Device model",
      "Operating-system version",
      "App version",
      "Crash timestamps",
      "Error messages",
      "Stack traces",
      "App-performance and diagnostic information",
      "Technical identifiers associated with a crash report",
    ],
    tail: "We use this information only to maintain the App, investigate errors, improve reliability, and prevent technical problems. We do not intentionally include your alarm settings, movement history, step counts, contact details, or other personal content in crash reports. Sentry processes diagnostic information on our behalf in accordance with its own Privacy Policy. Apple may also provide aggregated or device-level crash and diagnostic information when you have chosen to share analytics with app developers through your Apple device settings. Apple handles this information under the Apple Privacy Policy.",
  },
  {
    id: "analytics",
    title: "5. Analytics",
    body: [
      "Moove does not currently use information for advertising or cross-app tracking.",
      "We may introduce privacy-conscious product analytics in the future to understand how App features are used and to improve the user experience. We will update this Privacy Policy and any required App Store privacy disclosures before introducing analytics that materially changes our data practices.",
      "If legally required, we will also request consent or provide appropriate privacy controls before activating such analytics.",
    ],
  },
  {
    id: "payments",
    title: "6. Subscriptions and Payments",
    body: [
      "Moove offers subscriptions through Apple's App Store. A subscription may include a seven-day free trial followed by a monthly or annual paid subscription, depending on the option selected by the user.",
      "All payments, billing details, subscription renewals, and payment credentials are processed by Apple. OneDayBuild does not receive or store your complete payment-card or bank-account details.",
      "The App may receive limited transaction information from Apple—such as subscription status, product purchased, expiration status, and transaction identifiers—to determine whether you are entitled to access Moove.",
      "Apple processes purchase and subscription information under its own privacy terms and App Store policies.",
    ],
  },
  {
    id: "you-send",
    title: "7. Information You Send to Us",
    body: [
      "If you contact us by email, we will receive the email address you use and the information you choose to include in your communication.",
      "We use this information to:",
    ],
    bullets: [
      "Respond to your enquiry",
      "Provide customer support",
      "Investigate technical or billing issues",
      "Resolve complaints or disputes",
      "Meet legal, accounting, tax, or regulatory obligations",
      "Protect our legal rights",
    ],
    tail: "We retain support correspondence only for as long as reasonably necessary for the nature of the communication and any related legal, financial, security, or dispute-resolution requirements. Please do not include sensitive personal information in support emails unless it is necessary to resolve your issue.",
  },
  {
    id: "sharing",
    title: "8. How Information May Be Shared",
    body: ["We do not sell or rent personal information.", "Limited information may be processed or disclosed:"],
    bullets: [
      "To service providers such as Sentry, solely to operate, secure, or improve the App",
      "To Apple for App Store subscriptions, diagnostics, distribution, and related platform services",
      "When required by applicable law, regulation, legal process, or a valid government request",
      "When reasonably necessary to protect the rights, safety, property, or security of users, OneDayBuild, or others",
      "In connection with a merger, acquisition, restructuring, or transfer of the App, subject to appropriate confidentiality and legal protections",
    ],
    tail: "Service providers are permitted to process information only for the services they provide and according to their respective agreements and privacy obligations.",
  },
  {
    id: "retention",
    title: "9. Data Storage and Retention",
    body: [
      "Alarm settings, step goals, step progress, and related preferences are stored locally on your device.",
      "You can remove this locally stored information by deleting the App. Information may remain in device backups managed by Apple until those backups are deleted or expire under Apple's policies.",
      "Diagnostic information processed through Sentry is retained only for as long as reasonably necessary to investigate errors, maintain App security and reliability, and meet applicable legal obligations.",
      "Subscription information is retained and managed by Apple according to Apple's policies. We may retain limited transaction records where necessary for entitlement verification, accounting, fraud prevention, dispute resolution, or legal compliance.",
    ],
  },
  {
    id: "security",
    title: "10. Data Security",
    body: [
      "We use reasonable administrative and technical measures designed to protect information processed in connection with Moove.",
      "However, no device, software application, transmission method, or electronic storage system can be guaranteed to be completely secure. You are responsible for maintaining the security of your device and Apple Account.",
    ],
  },
  {
    id: "international",
    title: "11. International Processing",
    body: [
      "Moove is available worldwide. Our service providers may process limited diagnostic or transaction-related information in countries other than the country in which you live.",
      "Where required, we and our service providers use appropriate measures designed to protect information transferred internationally in accordance with applicable data-protection laws.",
    ],
  },
  {
    id: "rights",
    title: "12. Your Privacy Rights",
    body: [
      "Depending on where you live, you may have rights concerning personal information processed in connection with Moove, including the right to:",
    ],
    bullets: [
      "Request access to your personal information",
      "Request correction of inaccurate information",
      "Request deletion of eligible information",
      "Withdraw consent where processing is based on consent",
      "Object to or restrict certain processing",
      "Request a portable copy of eligible information",
      "Lodge a complaint with an applicable data-protection authority",
    ],
    tail: "Because Moove does not have user accounts, we may be unable to identify diagnostic information as belonging to a particular person. We may request reasonable information to verify and process a privacy request, but we will not collect additional information unnecessarily. To exercise an applicable privacy right, contact us at humans@onedaybuild.co.",
  },
  {
    id: "children",
    title: "13. Children's Privacy",
    body: [
      "Moove is a general-purpose alarm clock that may be used by people of different ages. It is not specifically directed at children and does not require users to submit names, contact details, dates of birth, or account information.",
      "We do not knowingly collect personal information from children for advertising, profiling, or behavioural tracking.",
      "If you are a parent or legal guardian and believe that a child has provided personal information to us—for example, through a support email—please contact us at humans@onedaybuild.co. We will review the request and delete the information where required by law.",
    ],
  },
  {
    id: "third-party",
    title: "14. Third-Party Services",
    body: ["Moove currently relies on services provided by:"],
    bullets: [
      "Apple: App distribution, subscriptions, payments, device permissions, local notifications, and optional diagnostic sharing",
      "Sentry: Crash reporting, error monitoring, and App reliability",
    ],
    tail: "These providers operate under their own terms and privacy policies. We encourage you to review their privacy information: Apple Privacy Policy and Sentry Privacy Policy.",
  },
  {
    id: "changes",
    title: "15. Changes to This Privacy Policy",
    body: [
      "We may update this Privacy Policy when the App's functionality, service providers, legal obligations, or information practices change.",
      "The updated policy will display a revised effective date. If a change materially affects how personal information is processed, we will provide additional notice or request consent where required by law.",
    ],
  },
  {
    id: "contact",
    title: "16. Contact Us",
    body: [
      "For questions, support requests, complaints, or privacy-related enquiries, contact:",
      "OneDayBuild — Sole proprietorship of Chetan Chopra, Goa, India. GSTIN: 30AVHPC0145J1Z7. Email: humans@onedaybuild.co",
    ],
  },
];

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="min-h-screen bg-cream">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 pb-24 pt-32 sm:px-6 sm:pt-40 lg:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-terra">
              Privacy Policy
            </p>
          </Reveal>
          <h1 className="mt-6 font-serif text-5xl leading-[1.05] tracking-tight text-ink sm:text-6xl">
            <MaskedLine delay={0.1}>Your privacy,</MaskedLine>
            <MaskedLine delay={0.22} className="italic text-terra">on your device.</MaskedLine>
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-6 text-lg leading-relaxed text-mutedwarm">
              Moove Alarm Clock ("Moove," "the App," "we," "us," or "our") is
              operated by OneDayBuild, a sole proprietorship owned by Chetan
              Chopra and registered in Goa, India under GSTIN 30AVHPC0145J1Z7.
            </p>
            <p className="mt-4 text-base leading-relaxed text-mutedwarm">
              We designed Moove to function without user accounts and to keep
              your alarm, movement, and step information on your device. This
              Privacy Policy explains what information the App accesses, how it
              is used, and the limited circumstances in which technical
              information may be processed by service providers.
            </p>
            <p className="mt-6 inline-block rounded-full border border-line bg-cardcream px-4 py-1.5 text-xs font-medium text-mutedwarm">
              Effective date: 15 September 2026
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-12 lg:grid-cols-[260px_1fr]">
          <aside className="hidden lg:block">
            <nav
              data-testid="privacy-toc"
              className="sticky top-28 space-y-2 border-l border-line pl-5"
            >
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-mutedwarm">
                Contents
              </p>
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  data-testid={`toc-${s.id}`}
                  className="block text-sm text-mutedwarm transition-colors duration-200 hover:text-terra"
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </aside>

          <div className="max-w-3xl space-y-2">
            {sections.map((s, i) => (
              <Reveal key={s.id} delay={Math.min(i * 0.03, 0.2)}>
                <section id={s.id} data-testid={`privacy-section-${s.id}`} className="scroll-mt-28 border-t border-line py-10">
                  <h2 className="font-serif text-2xl text-ink sm:text-3xl">{s.title}</h2>
                  {s.body.map((p, j) => (
                    <p key={j} className="mt-4 text-base leading-relaxed text-mutedwarm">
                      {p}
                    </p>
                  ))}
                  {s.bullets && (
                    <ul className="mt-4 space-y-2.5">
                      {s.bullets.map((b, j) => (
                        <li key={j} className="flex gap-3 text-base leading-relaxed text-mutedwarm">
                          <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-terra" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.tail && (
                    <p className="mt-4 text-base leading-relaxed text-mutedwarm">{s.tail}</p>
                  )}
                </section>
              </Reveal>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
